import React, { useState, useEffect } from "react";
import {
  MdEdit,
  MdDeleteOutline,
  MdSearch,
  MdClose,
  MdAdd,
  MdCheckCircle,
  MdWarning,
  MdClass,
  MdCloudUpload
} from "react-icons/md";

const API_BASE = "https://api.visiontrackcrm.com";

const resolveImageUrl = (url) => {
  if (!url || typeof url !== "string") return url;
  if (url.startsWith("data:") || url.startsWith("blob:") || url.startsWith("file:")) return url;
  if (url.startsWith("/")) return `${API_BASE}${url}`;
  if (url.includes("/images/")) {
    const path = url.substring(url.indexOf("/images/"));
    return `${API_BASE}${path}`;
  }
  return url;
};

function Brands() {
  const [name, setName] = useState("");
  const [logo, setLogo] = useState(null);
  const [preview, setPreview] = useState("");
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Edit State
  const [editingBrand, setEditingBrand] = useState(null);
  const [editName, setEditName] = useState("");
  const [editLogo, setEditLogo] = useState(null);
  const [editPreview, setEditPreview] = useState("");
  const [editLoading, setEditLoading] = useState(false);

  // Delete State
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Toast / Alert
  const [toast, setToast] = useState(null);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => {
      setToast(null);
    }, 3500);
  };

  useEffect(() => {
    loadBrands();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadBrands = async () => {
    try {
      const res = await fetch(`${API_BASE}/brands/all`);
      const data = await res.json();
      if (data.success) {
        setBrands(data.data || []);
      }
    } catch (error) {
      console.error(error);
      showToast("Failed to load brands", "error");
    }
  };

  // Select Image for Add
  const handleImage = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogo(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  // Clear Image for Add
  const clearAddImage = () => {
    setLogo(null);
    setPreview("");
  };

  // Add Brand
  const addBrand = async (e) => {
    if (e) e.preventDefault();
    if (!name.trim()) {
      showToast("Please enter a brand name", "error");
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("name", name.trim());
      if (logo) {
        formData.append("logo", logo);
      }

      const res = await fetch(`${API_BASE}/brands/add`, {
        method: "POST",
        body: formData
      });
      const data = await res.json();

      if (data.success) {
        showToast("Brand added successfully!", "success");
        setName("");
        setLogo(null);
        setPreview("");
        loadBrands();
      } else {
        showToast(data.message || "Failed to add brand", "error");
      }
    } catch (error) {
      console.error(error);
      showToast("Server error while adding brand", "error");
    } finally {
      setLoading(false);
    }
  };

  // Open Edit Modal
  const openEditModal = (b) => {
    setEditingBrand(b);
    setEditName(b.name || "");
    setEditLogo(null);
    setEditPreview(b.logo || "");
  };

  // Close Edit Modal
  const closeEditModal = () => {
    setEditingBrand(null);
    setEditName("");
    setEditLogo(null);
    setEditPreview("");
  };

  // Select Image for Edit
  const handleEditImage = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditLogo(file);
      setEditPreview(URL.createObjectURL(file));
    }
  };

  // Save Edit
  const handleUpdateBrand = async (e) => {
    if (e) e.preventDefault();
    if (!editName.trim()) {
      showToast("Brand name cannot be empty", "error");
      return;
    }

    try {
      setEditLoading(true);
      const formData = new FormData();
      formData.append("name", editName.trim());
      if (editLogo) {
        formData.append("logo", editLogo);
      }

      const res = await fetch(`${API_BASE}/brands/update/${editingBrand.id}`, {
        method: "PUT",
        body: formData
      });
      const data = await res.json();

      if (data.success) {
        showToast("Brand updated successfully!", "success");
        closeEditModal();
        loadBrands();
      } else {
        showToast(data.message || "Failed to update brand", "error");
      }
    } catch (error) {
      console.error(error);
      showToast("Server error while updating brand", "error");
    } finally {
      setEditLoading(false);
    }
  };

  // Open Delete Modal
  const openDeleteModal = (b) => {
    setDeleteTarget(b);
  };

  // Close Delete Modal
  const closeDeleteModal = () => {
    setDeleteTarget(null);
  };

  // Confirm Delete
  const handleDeleteBrand = async () => {
    if (!deleteTarget) return;

    try {
      setDeleteLoading(true);
      const res = await fetch(`${API_BASE}/brands/delete/${deleteTarget.id}`, {
        method: "DELETE"
      });
      const data = await res.json();

      if (data.success) {
        showToast("Brand deleted successfully!", "success");
        closeDeleteModal();
        loadBrands();
      } else {
        showToast(data.message || "Failed to delete brand", "error");
      }
    } catch (error) {
      console.error(error);
      showToast("Server error while deleting brand", "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  // Filtered brands
  const filteredBrands = brands.filter((b) =>
    b.name ? b.name.toLowerCase().includes(searchTerm.toLowerCase()) : false
  );

  return (
    <div className="brand-page-container">
      <style>{`
        .brand-page-container {
          padding: 24px;
          background: #f8fafc;
          min-height: 100vh;
          font-family: 'Inter', sans-serif;
          box-sizing: border-box;
        }

        /* HEADER */
        .brand-header {
          background: linear-gradient(135deg, #0067b1 0%, #1e40af 100%);
          padding: 26px 30px;
          border-radius: 20px;
          color: #ffffff;
          margin-bottom: 24px;
          box-shadow: 0 10px 24px rgba(0, 103, 177, 0.15);
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 16px;
        }

        .brand-header-title {
          margin: 0;
          font-size: 1.6rem;
          font-weight: 800;
          letter-spacing: -0.02em;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .brand-header-sub {
          margin: 6px 0 0 0;
          font-size: 0.9rem;
          color: #e0f2fe;
        }

        .brand-header-badge {
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(8px);
          padding: 8px 16px;
          border-radius: 9999px;
          font-size: 0.85rem;
          font-weight: 700;
          color: #ffffff;
          border: 1px solid rgba(255, 255, 255, 0.3);
        }

        /* TOAST */
        .brand-toast {
          position: fixed;
          top: 24px;
          right: 24px;
          z-index: 1000;
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 14px 20px;
          border-radius: 12px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
          animation: slideIn 0.3s ease;
          font-weight: 600;
          font-size: 0.9rem;
        }

        .brand-toast.success {
          background: #ecfdf5;
          color: #065f46;
          border: 1px solid #a7f3d0;
        }

        .brand-toast.error {
          background: #fef2f2;
          color: #991b1b;
          border: 1px solid #fecaca;
        }

        @keyframes slideIn {
          from {
            transform: translateY(-20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        /* CARD FOR ADD BRAND */
        .brand-form-card {
          background: #ffffff;
          padding: 24px;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.03);
          margin-bottom: 24px;
        }

        .brand-form-title {
          font-size: 1.1rem;
          font-weight: 700;
          color: #0f172a;
          margin: 0 0 16px 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .brand-form-grid {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
          align-items: center;
        }

        .brand-input {
          height: 48px;
          padding: 0 16px;
          border: 1.5px solid #cbd5e1;
          border-radius: 10px;
          font-size: 0.92rem;
          color: #1e293b;
          outline: none;
          transition: all 0.2s ease;
          flex: 1;
          min-width: 260px;
        }

        .brand-input:focus {
          border-color: #0067b1;
          box-shadow: 0 0 0 3px rgba(0, 103, 177, 0.12);
        }

        .brand-upload-box {
          height: 48px;
          padding: 0 18px;
          border: 1.5px dashed #94a3b8;
          border-radius: 10px;
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          background: #f8fafc;
          transition: all 0.2s ease;
          position: relative;
          color: #475569;
          font-size: 0.88rem;
          font-weight: 500;
        }

        .brand-upload-box:hover {
          border-color: #0067b1;
          background: #f0f7ff;
          color: #0067b1;
        }

        .brand-file-input {
          position: absolute;
          inset: 0;
          opacity: 0;
          width: 100%;
          height: 100%;
          cursor: pointer;
        }

        .brand-preview-mini {
          width: 32px;
          height: 32px;
          border-radius: 6px;
          object-fit: contain;
          border: 1px solid #e2e8f0;
          background: #ffffff;
        }

        .brand-btn-add {
          height: 48px;
          padding: 0 24px;
          background: linear-gradient(135deg, #0067b1 0%, #1e40af 100%);
          color: #ffffff;
          border: none;
          border-radius: 10px;
          font-size: 0.92rem;
          font-weight: 700;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          transition: all 0.2s ease;
          box-shadow: 0 4px 12px rgba(0, 103, 177, 0.2);
        }

        .brand-btn-add:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0, 103, 177, 0.3);
        }

        .brand-btn-add:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        /* SECTION CONTROLS (SEARCH & COUNT) */
        .brand-section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 14px;
          margin-bottom: 18px;
        }

        .brand-section-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: #0f172a;
          margin: 0;
        }

        .brand-search-box {
          display: flex;
          align-items: center;
          gap: 8px;
          background: #ffffff;
          border: 1px solid #cbd5e1;
          border-radius: 10px;
          padding: 8px 14px;
          width: 260px;
        }

        .brand-search-box input {
          border: none;
          outline: none;
          background: transparent;
          font-size: 0.88rem;
          color: #334155;
          width: 100%;
        }

        /* GRID */
        .brand-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 18px;
        }

        /* BRAND CARD */
        .brand-item-card {
          background: #ffffff;
          padding: 18px;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
          transition: all 0.2s ease;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          gap: 14px;
        }

        .brand-item-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.06);
          border-color: #cbd5e1;
        }

        .brand-item-top {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .brand-logo-box {
          width: 56px;
          height: 56px;
          border-radius: 12px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          flex-shrink: 0;
        }

        .brand-logo-img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          padding: 4px;
          box-sizing: border-box;
        }

        .brand-fallback-icon {
          font-size: 1.8rem;
          color: #94a3b8;
        }

        .brand-info {
          flex: 1;
          overflow: hidden;
        }

        .brand-item-name {
          margin: 0;
          font-size: 1rem;
          font-weight: 700;
          color: #0f172a;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .brand-item-id {
          margin: 4px 0 0 0;
          font-size: 0.78rem;
          color: #64748b;
          font-weight: 500;
        }

        .brand-item-actions {
          display: flex;
          align-items: center;
          gap: 8px;
          border-top: 1px solid #f1f5f9;
          padding-top: 12px;
        }

        .brand-btn-action {
          flex: 1;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 8px 12px;
          border-radius: 8px;
          font-size: 0.82rem;
          font-weight: 600;
          cursor: pointer;
          border: 1px solid transparent;
          transition: all 0.2s ease;
        }

        .brand-btn-edit {
          background: #f0f7ff;
          color: #0067b1;
          border-color: #bfdbfe;
        }

        .brand-btn-edit:hover {
          background: #0067b1;
          color: #ffffff;
        }

        .brand-btn-delete {
          background: #fff1f2;
          color: #e11d48;
          border-color: #fecdd3;
        }

        .brand-btn-delete:hover {
          background: #e11d48;
          color: #ffffff;
        }

        /* EMPTY STATE */
        .brand-empty {
          background: #ffffff;
          border-radius: 16px;
          padding: 48px 24px;
          text-align: center;
          color: #64748b;
          border: 1px dashed #cbd5e1;
        }

        /* MODAL STYLES */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(15, 23, 42, 0.45);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .modal-card {
          background: #ffffff;
          border-radius: 18px;
          width: 100%;
          max-width: 500px;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
          overflow: hidden;
          animation: modalPop 0.25s ease;
        }

        @keyframes modalPop {
          from {
            transform: scale(0.94);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }

        .modal-header {
          padding: 20px 24px;
          border-bottom: 1px solid #f1f5f9;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .modal-header h3 {
          margin: 0;
          font-size: 1.15rem;
          font-weight: 700;
          color: #0f172a;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .modal-close-btn {
          background: transparent;
          border: none;
          color: #94a3b8;
          cursor: pointer;
          font-size: 1.3rem;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 4px;
          border-radius: 8px;
        }

        .modal-close-btn:hover {
          color: #0f172a;
          background: #f1f5f9;
        }

        .modal-body {
          padding: 24px;
        }

        .modal-footer {
          padding: 16px 24px;
          border-top: 1px solid #f1f5f9;
          background: #f8fafc;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 12px;
        }

        .btn-cancel {
          padding: 9px 18px;
          background: #ffffff;
          border: 1px solid #cbd5e1;
          color: #475569;
          border-radius: 8px;
          font-size: 0.88rem;
          font-weight: 600;
          cursor: pointer;
        }

        .btn-cancel:hover {
          background: #f1f5f9;
        }

        .btn-save {
          padding: 9px 20px;
          background: #0067b1;
          color: #ffffff;
          border: none;
          border-radius: 8px;
          font-size: 0.88rem;
          font-weight: 600;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }

        .btn-save:hover {
          background: #1e40af;
        }

        .btn-save:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-danger {
          padding: 9px 20px;
          background: #e11d48;
          color: #ffffff;
          border: none;
          border-radius: 8px;
          font-size: 0.88rem;
          font-weight: 600;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }

        .btn-danger:hover {
          background: #be123c;
        }

        .btn-danger:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .edit-logo-section {
          margin-top: 18px;
        }

        .edit-logo-preview-box {
          width: 90px;
          height: 90px;
          border-radius: 12px;
          border: 1.5px dashed #cbd5e1;
          background: #f8fafc;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          position: relative;
          margin-bottom: 10px;
        }

        .edit-logo-preview-img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          padding: 6px;
          box-sizing: border-box;
        }
      `}</style>

      {/* TOAST ALERT */}
      {toast && (
        <div className={`brand-toast ${toast.type}`}>
          {toast.type === "success" ? (
            <MdCheckCircle style={{ fontSize: "1.2rem" }} />
          ) : (
            <MdWarning style={{ fontSize: "1.2rem" }} />
          )}
          <span>{toast.message}</span>
        </div>
      )}

      {/* HEADER */}
      <div className="brand-header">
        <div>
          <h2 className="brand-header-title">
            <MdClass /> Brand Management
          </h2>
          <p className="brand-header-sub">
            Add, update, and manage optical brands and logos
          </p>
        </div>
        <div className="brand-header-badge">
          Total Brands: {brands.length}
        </div>
      </div>

      {/* ADD BRAND CARD */}
      <div className="brand-form-card">
        <h3 className="brand-form-title">
          <MdAdd style={{ color: "#0067b1", fontSize: "1.3rem" }} /> Add New Brand
        </h3>
        <form onSubmit={addBrand} className="brand-form-grid">
          <input
            className="brand-input"
            placeholder="Brand Name (e.g. Ray-Ban, Oakley)"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          <label className="brand-upload-box">
            {preview ? (
              <>
                <img src={preview} alt="New brand logo preview" className="brand-preview-mini" />
                <span>Change Logo</span>
              </>
            ) : (
              <>
                <MdCloudUpload style={{ fontSize: "1.3rem", color: "#0067b1" }} />
                <span>Upload Logo</span>
              </>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={handleImage}
              className="brand-file-input"
            />
          </label>

          {preview && (
            <button
              type="button"
              onClick={clearAddImage}
              style={{
                border: "none",
                background: "#f1f5f9",
                color: "#64748b",
                borderRadius: "8px",
                padding: "8px 12px",
                cursor: "pointer",
                fontSize: "0.82rem",
                fontWeight: 600
              }}
            >
              Remove Logo
            </button>
          )}

          <button type="submit" className="brand-btn-add" disabled={loading}>
            <MdAdd style={{ fontSize: "1.2rem" }} />
            {loading ? "Adding..." : "Add Brand"}
          </button>
        </form>
      </div>

      {/* SECTION HEADER & SEARCH */}
      <div className="brand-section-header">
        <h3 className="brand-section-title">All Brands ({filteredBrands.length})</h3>
        <div className="brand-search-box">
          <MdSearch style={{ color: "#94a3b8", fontSize: "1.2rem" }} />
          <input
            type="text"
            placeholder="Search brands..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              style={{
                border: "none",
                background: "transparent",
                cursor: "pointer",
                color: "#94a3b8",
                display: "flex",
                alignItems: "center"
              }}
            >
              <MdClose />
            </button>
          )}
        </div>
      </div>

      {/* BRANDS GRID */}
      {filteredBrands.length === 0 ? (
        <div className="brand-empty">
          <MdClass style={{ fontSize: "2.5rem", color: "#cbd5e1", marginBottom: "10px" }} />
          <p style={{ margin: 0, fontWeight: 600 }}>
            {searchTerm ? "No brands match your search." : "No brands found. Add one above!"}
          </p>
        </div>
      ) : (
        <div className="brand-grid">
          {filteredBrands.map((item) => (
            <div key={item.id} className="brand-item-card">
              <div className="brand-item-top">
                <div className="brand-logo-box">
                  {item.logo ? (
                    <img
                      src={resolveImageUrl(item.logo)}
                      alt={`${item.name || "Brand"} logo`}
                      className="brand-logo-img"
                    />
                  ) : (
                    <MdClass className="brand-fallback-icon" />
                  )}
                </div>

                <div className="brand-info">
                  <h4 className="brand-item-name" title={item.name}>
                    {item.name}
                  </h4>
                  <p className="brand-item-id">ID: #{item.id}</p>
                </div>
              </div>

              <div className="brand-item-actions">
                <button
                  type="button"
                  className="brand-btn-action brand-btn-edit"
                  onClick={() => openEditModal(item)}
                >
                  <MdEdit style={{ fontSize: "1rem" }} />
                  Edit
                </button>
                <button
                  type="button"
                  className="brand-btn-action brand-btn-delete"
                  onClick={() => openDeleteModal(item)}
                >
                  <MdDeleteOutline style={{ fontSize: "1rem" }} />
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* EDIT MODAL */}
      {editingBrand && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <MdEdit style={{ color: "#0067b1" }} /> Edit Brand
              </h3>
              <button className="modal-close-btn" onClick={closeEditModal}>
                <MdClose />
              </button>
            </div>

            <form onSubmit={handleUpdateBrand}>
              <div className="modal-body">
                <label
                  style={{
                    display: "block",
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    color: "#475569",
                    marginBottom: "8px"
                  }}
                >
                  Brand Name
                </label>
                <input
                  className="brand-input"
                  style={{ width: "100%", boxSizing: "border-box", marginBottom: "18px" }}
                  value={editName}
                  autoFocus
                  placeholder="Enter brand name"
                  onChange={(e) => setEditName(e.target.value)}
                />

                <div className="edit-logo-section">
                  <label
                    style={{
                      display: "block",
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      color: "#475569",
                      marginBottom: "8px"
                    }}
                  >
                    Brand Logo
                  </label>
                  <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                    <div className="edit-logo-preview-box">
                      {editPreview ? (
                        <img
                          src={resolveImageUrl(editPreview)}
                          alt="Brand logo preview"
                          className="edit-logo-preview-img"
                        />
                      ) : (
                        <MdClass style={{ fontSize: "2rem", color: "#cbd5e1" }} />
                      )}
                    </div>

                    <label className="brand-upload-box">
                      <MdCloudUpload style={{ fontSize: "1.3rem", color: "#0067b1" }} />
                      <span>{editPreview ? "Replace Logo" : "Upload Logo"}</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleEditImage}
                        className="brand-file-input"
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={closeEditModal}
                  disabled={editLoading}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-save" disabled={editLoading}>
                  {editLoading ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DELETE CONFIRMATION MODAL */}
      {deleteTarget && (
        <div className="modal-overlay" onClick={closeDeleteModal}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ color: "#e11d48" }}>
                <MdWarning style={{ color: "#e11d48" }} /> Confirm Delete
              </h3>
              <button className="modal-close-btn" onClick={closeDeleteModal}>
                <MdClose />
              </button>
            </div>

            <div className="modal-body">
              <p style={{ margin: "0 0 12px 0", color: "#334155", fontSize: "0.95rem" }}>
                Are you sure you want to delete brand{" "}
                <strong style={{ color: "#0f172a" }}>"{deleteTarget.name}"</strong> (ID: #{deleteTarget.id})?
              </p>
              <p style={{ margin: 0, color: "#64748b", fontSize: "0.85rem" }}>
                This action cannot be undone. Products associated with this brand may be affected.
              </p>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn-cancel"
                onClick={closeDeleteModal}
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={handleDeleteBrand}
                disabled={deleteLoading}
              >
                <MdDeleteOutline style={{ fontSize: "1.1rem" }} />
                {deleteLoading ? "Deleting..." : "Delete Brand"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Brands;